import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { ChevronDown } from "lucide-react";

import { Image, NavbarRoute, LogOutButton } from "components/atoms";

import { NavRoute } from "models/routes";

import { navRoutes } from "utils/routes";

import { SIZES } from "styles/size";

import logoSm from "assets/images/logoSm.svg";
import logo from "assets/images/logo.svg";

interface Props {
	isExpanded: boolean;
}

const baseClasses =
	"h-8 w-full flex items-center text-[hsl(var(--sidebar-foreground))] text-sm no-underline rounded-md transition-colors";

const activeClasses =
	"bg-primary text-primary-foreground font-medium hover:bg-primary/90";

const inactiveClasses =
	"bg-transparent hover:bg-[hsl(var(--sidebar-accent))] hover:text-[hsl(var(--sidebar-accent-foreground))]";

const Navbar = (props: Props): JSX.Element => {
	const location = useLocation();
	const { isExpanded } = props;

	const [expandedParents, setExpandedParents] = useState<Set<string>>(() => {
		const initial = new Set<string>();
		navRoutes.forEach((route) => {
			if (
				route.children &&
				route.children.some(
					(c) =>
						location.pathname === c.to ||
						location.pathname.startsWith(`${c.to}/`)
				)
			) {
				initial.add(route.to);
			}
		});
		return initial;
	});

	const toggleParent = (to: string) => {
		setExpandedParents((prev) => {
			const next = new Set(prev);
			if (next.has(to)) {
				next.delete(to);
			} else {
				next.add(to);
			}
			return next;
		});
	};

	const renderRoutes = () =>
		navRoutes.map((route: NavRoute) => {
			if (route.children) {
				const isChildActive = route.children.some(
					(c) =>
						location.pathname === c.to ||
						location.pathname.startsWith(`${c.to}/`)
				);
				const isOpen = expandedParents.has(route.to) || isChildActive;

				return (
					<div key={route.to}>
						<button
							type="button"
							onClick={() => toggleParent(route.to)}
							aria-expanded={isOpen}
							aria-label={!isExpanded ? route.title : undefined}
							className={`${baseClasses} ${
								isChildActive ? activeClasses : inactiveClasses
							} border-none cursor-pointer p-0 ${
								isExpanded
									? "px-2 justify-between"
									: "justify-center"
							}`}
						>
							<span className="flex items-center">
								{route.icon ? (
									<div
										className="flex items-center justify-center"
										style={{
											marginRight: isExpanded ? 8 : 0,
										}}
									>
										<route.icon className="h-4 w-4 shrink-0" />
									</div>
								) : null}
								{isExpanded ? route.title : null}
							</span>
							{isExpanded ? (
								<ChevronDown
									className={`h-3.5 w-3.5 transition-transform ${
										isOpen ? "rotate-180" : ""
									}`}
								/>
							) : null}
						</button>
						{isOpen && (
							<div
								className={
									isExpanded ? "ml-6 mt-0.5 space-y-0.5" : "mt-0.5 space-y-0.5"
								}
							>
								{route.children.map((child) => {
									const childActive =
										location.pathname === child.to ||
										location.pathname.startsWith(
											`${child.to}/`
										);

									return (
										<NavLink
											key={child.to}
											to={child.to}
											aria-label={!isExpanded ? child.title : undefined}
											className={`${baseClasses} ${
												childActive
													? activeClasses
													: inactiveClasses
											} ${
												isExpanded
													? "px-2"
													: "justify-center"
											} text-xs`}
										>
											{isExpanded ? (
												child.title
											) : (
												<span className="text-[10px] leading-none">
													{child.title
														.charAt(0)
														.toUpperCase()}
												</span>
											)}
										</NavLink>
									);
								})}
							</div>
						)}
					</div>
				);
			}

			const isActive =
				route.to === "/"
					? location.pathname === "/"
					: location.pathname.startsWith(route.to);

			return (
				<NavbarRoute
					route={route}
					key={route.to}
					isActive={isActive}
					isExpanded={isExpanded}
				/>
			);
		});

	return (
		<div
			className="h-screen p-2 bg-[hsl(var(--sidebar-background))] z-[1] transition-[width] duration-[250ms] ease relative flex flex-col gap-1"
			style={{
				width: isExpanded
					? `${SIZES.EXPANDED_NAVBAR_WIDTH}px`
					: `${SIZES.NAVBAR_WIDTH}px`,
			}}
		>
			<div className="w-full flex justify-center mb-4 py-3 h-11 invert dark:invert-0">
				<Image
					src={isExpanded ? logo : logoSm}
					width={
						isExpanded
							? SIZES.EXPANDED_NAVBAR_LOGO_WIDTH
							: SIZES.NAVBAR_LOGO_WIDTH
					}
				/>
			</div>
			{renderRoutes()}
			<div className="mt-auto w-full">
				<LogOutButton isExpanded={isExpanded} />
			</div>
		</div>
	);
};

export default Navbar;
